/**
 * PostTrialScreen — ekran po zakończeniu trialu
 * Pokazuje statystyki z trialu + presja + plan PRO / PREMIUM
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Eye, Zap, Crown, ArrowRight, PauseCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export default function PostTrialScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/billing/trial-summary`, { headers: h }).catch(() => ({ data: {} })),
      axios.get(`${API}/billing/plans`).catch(() => ({ data: { plans: [] } })),
    ]).then(([sumRes, plansRes]) => {
      setSummary(sumRes.data);
      setPlans(plansRes.data?.plans || []);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
      <div className="w-8 h-8 border-3 border-[#F47A3B] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const stats = [
    { label: 'Zapytania od klientów', value: summary?.leads_count || 0, icon: Users, color: '#F47A3B' },
    { label: 'Wyświetlenia strony', value: summary?.views_count || 0, icon: Eye, color: '#3B82F6' },
    { label: 'Kampanie uruchomione', value: summary?.campaigns_count || 0, icon: TrendingUp, color: '#10B981' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        {/* Stopped badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6"
          data-testid="campaign-stopped-badge"
        >
          <PauseCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-800 font-semibold text-sm">Twoja kampania została zatrzymana</span>
        </motion.div>

        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1A1D26] to-[#2D3142] px-8 py-8 text-center">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-white mb-2"
            >
              Twoja promocja działała przez {summary?.trial_days || 7} dni
            </motion.h1>
            <p className="text-zinc-400 text-sm">To dopiero początek. Zobacz co udało się osiągnąć.</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="p-5 text-center"
              >
                <s.icon className="w-5 h-5 mx-auto mb-2" style={{ color: s.color }} />
                <p className="text-2xl font-bold text-zinc-900" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>{s.value}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{s.label}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA text */}
          <div className="px-8 py-6 text-center border-b border-gray-100">
            <h2 className="text-lg font-bold text-zinc-900 mb-1">Chcesz dalej zdobywać klientów?</h2>
            <p className="text-sm text-zinc-500">Odblokuj promocję i wróć do zdobywania klientów.</p>
          </div>

          {/* Plan cards */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {plans.filter(p => !p.coming_soon && p.price).map((plan) => {
                const isPro = plan.popular;
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className={`rounded-2xl p-5 border-2 transition-all cursor-pointer hover:shadow-lg ${
                      isPro
                        ? 'border-[#F47A3B] bg-[#FFFBF7] shadow-md'
                        : 'border-zinc-200 bg-white hover:border-zinc-300'
                    }`}
                    onClick={() => navigate(`/panel/cennik?plan=${plan.id}`)}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    {isPro && (
                      <span className="inline-block text-[10px] font-bold bg-[#F47A3B] text-white px-2.5 py-0.5 rounded-full mb-3 uppercase tracking-wider">
                        {plan.headline || 'Najczęściej wybierany'}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      {isPro ? <Zap className="w-5 h-5 text-[#F47A3B]" /> : <Crown className="w-5 h-5 text-[#6366F1]" />}
                      <span className="font-bold text-zinc-900 text-lg">{plan.name}</span>
                    </div>
                    <p className="text-2xl font-bold text-zinc-900 mb-1">
                      {plan.price_monthly} <span className="text-sm font-normal text-zinc-400">zł/mies.</span>
                    </p>
                    <p className="text-xs text-zinc-500 mb-3">{plan.subtitle || ''}</p>
                    <ul className="space-y-1.5">
                      {(plan.features || []).slice(0, 4).map((f, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-zinc-600">
                          <Check className="w-3 h-3 text-[#10B981] mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full mt-4 rounded-xl font-semibold text-sm ${
                        isPro
                          ? 'bg-[#F47A3B] hover:bg-[#E06A2B] text-white'
                          : 'bg-[#1A1D26] hover:bg-[#2D3142] text-white'
                      }`}
                      data-testid={`plan-cta-${plan.id}`}
                    >
                      Wybierz {plan.name} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-400 mt-4">
          Masz pytania? <a href="https://wa.me/48507334540?text=Hej%2C%20mam%20pytanie%20o%20ApisAI" target="_blank" rel="noopener noreferrer" className="text-[#F47A3B] hover:underline">Napisz do nas</a>
        </p>
      </motion.div>
    </div>
  );
}
